/* <collection-showcase> — home collections stage.
   A JSON feed (rendered by sections/temple-collections.liquid) drives a swap
   stage: each collection shows its background image, heading / description /
   CTA, and a scrollable track of its product coins. The pager (arrows + dots)
   switches collections with a reveal-wipe; the coin track scrolls continuously
   with a progress bar. Coins are real product links/images. */
class CollectionShowcase extends HTMLElement {
  connectedCallback() {
    if (this._init) return;
    this._init = true;
    var dataEl = this.querySelector('.collection-showcase__data');
    this.data = dataEl ? JSON.parse(dataEl.textContent) : { collections: [] };
    this.list = this.data.collections || [];
    if (!this.list.length) return;
    this.index = 0;
    this.reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.base = this.querySelector('[data-base]');
    this.content = this.querySelector('[data-content]');
    this.track = this.querySelector('[data-track]');
    this.bottom = this.querySelector('.collection-showcase__bottom');
    this.dots = this.querySelector('[data-dots]');
    this.progress = this.querySelector('[data-progress]');
    this.thumb = this.querySelector('[data-thumb]');

    var self = this;
    // build collection dots
    this.dots.innerHTML = this.list.map(function (c, i) {
      return '<button class="collection-showcase__dot' + (i === 0 ? ' is-active' : '') + '" data-i="' + i +
        '" type="button" role="tab" aria-label="' + self.esc(c.name) + '" aria-selected="' + (i === 0) + '"></button>';
    }).join('');
    this.dotEls = Array.prototype.slice.call(this.dots.querySelectorAll('.collection-showcase__dot'));
    this.dotEls.forEach(function (d) { d.addEventListener('click', function () { self.go(+d.dataset.i); }); });

    var prev = this.querySelector('[data-prev]'), next = this.querySelector('[data-next]');
    if (prev) prev.addEventListener('click', function () { self.go(self.index - 1); });
    if (next) next.addEventListener('click', function () { self.go(self.index + 1); });
    var pager = this.querySelector('.collection-showcase__pager');
    if (pager) pager.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') { e.preventDefault(); self.go(self.index - 1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); self.go(self.index + 1); }
    });

    this.wireTrack();
    this._onResize = function () { self.updateProgress(); };
    addEventListener('resize', this._onResize);

    this.setCollection(0, true);
  }

  disconnectedCallback() { if (this._onResize) removeEventListener('resize', this._onResize); }

  esc(s) { return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); }

  go(i) {
    var n = this.list.length;
    i = ((i % n) + n) % n;
    if (i === this.index) return;
    this.setCollection(i);
  }

  setCollection(i, instant) {
    clearTimeout(this._swapT);             // cancel any in-flight swap so clicks stay responsive
    this.index = i;
    var c = this.list[i], self = this;
    this.dotEls.forEach(function (d, di) {
      d.classList.toggle('is-active', di === i);
      d.setAttribute('aria-selected', di === i ? 'true' : 'false');
    });
    if (instant) {
      this.base.style.backgroundImage = c.bg ? "url('" + c.bg + "')" : '';
      this.fillContent(c);
      this.fillTrack(c);
      return;
    }
    this.revealBg(c.bg);
    this.content.classList.add('is-out');
    this.track.classList.add('is-out');
    this._swapT = setTimeout(function () {
      self.fillContent(c);
      self.fillTrack(c);
      self.content.classList.remove('is-out');
      self.track.classList.remove('is-out');
    }, 240);
  }

  revealBg(bgUrl) {
    if (this._ov && this._ov.parentNode) { this._ov.remove(); }   // drop a still-animating prior wipe
    this._ov = null;
    if (!bgUrl) { this.base.style.backgroundImage = ''; return; }
    var self = this;
    if (this.reduce) { this.base.style.backgroundImage = "url('" + bgUrl + "')"; return; }
    var ov = document.createElement('div');
    this._ov = ov;
    ov.className = 'collection-showcase__bg';
    ov.style.backgroundImage = "url('" + bgUrl + "')";
    ov.style.zIndex = 2;
    ov.style.clipPath = 'inset(100% 0 0 0)';
    ov.style.transition = 'clip-path .62s cubic-bezier(.4,0,.2,1)';
    this.base.after(ov);
    requestAnimationFrame(function () { requestAnimationFrame(function () { ov.style.clipPath = 'inset(0 0 0 0)'; }); });
    var done = function () { self.base.style.backgroundImage = "url('" + bgUrl + "')"; if (ov.parentNode) ov.remove(); if (self._ov === ov) self._ov = null; };
    ov.addEventListener('transitionend', done, { once: true });
    setTimeout(done, 900);
  }

  fillContent(c) {
    var cta = c.cta
      ? '<a class="collection-showcase__cta" href="' + (c.url || '#') + '">' + this.esc(c.cta) +
        '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M4 12h15M13 6l6 6-6 6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg></a>'
      : '';
    this.content.innerHTML =
      '<h2 class="collection-showcase__title">' + this.esc(c.name) + '</h2>' +
      (c.desc ? '<p class="collection-showcase__desc">' + this.esc(c.desc) + '</p>' : '') + cta;
  }

  fillTrack(c) {
    var coins = c.coins || [], self = this;
    this.track.innerHTML = coins.map(function (coin) {
      return '<a class="collection-showcase__card" href="' + (coin.url || '#') + '">' +
        '<div class="collection-showcase__coin"><img class="js-fade" src="' + coin.image + '" alt="' + self.esc(coin.name) + '" loading="lazy"></div>' +
        '<div class="collection-showcase__card-title">' + self.esc(coin.name) + '</div></a>';
    }).join('');
    this.track.scrollLeft = 0;
    if (window.fadeInImages) window.fadeInImages(this.track);
    this.updateProgress();
  }

  /* coin track: continuous scroll + mouse drag */
  wireTrack() {
    var self = this, down = false, dragging = false, startX = 0, startScroll = 0, pid = null;
    this.track.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse') return;      // touch uses native scroll
      down = true; dragging = false; self._moved = false;
      startX = e.clientX; startScroll = self.track.scrollLeft; pid = e.pointerId;
    });
    this.track.addEventListener('pointermove', function (e) {
      if (!down) return;
      var dx = e.clientX - startX;
      if (!dragging) {
        if (Math.abs(dx) < 4) return;             // below threshold — stays a click
        // Only NOW capture the pointer. Capturing on pointerdown would retarget
        // the click to the track and a plain click would never reach the coin's
        // <a>, so the link wouldn't open. Capturing only once a real drag starts
        // keeps clicks navigable while still tracking a fast drag off the row.
        dragging = true; self._moved = true;
        self.track.classList.add('is-dragging');
        try { self.track.setPointerCapture(pid); } catch (err) {}
      }
      self.track.scrollLeft = startScroll - dx;
    });
    var end = function () {
      down = false; dragging = false;
      self.track.classList.remove('is-dragging');
      if (pid != null) { try { self.track.releasePointerCapture(pid); } catch (err) {} pid = null; }
    };
    this.track.addEventListener('pointerup', end);
    this.track.addEventListener('pointercancel', end);
    // don't follow a card link if the pointer was dragging the row
    this.track.addEventListener('click', function (e) { if (self._moved) { e.preventDefault(); self._moved = false; } }, true);
    this.track.addEventListener('scroll', function () { self.updateProgress(); }, { passive: true });
  }

  updateProgress() {
    var t = this.track;
    if (!t) return;
    var fits = t.scrollWidth <= t.clientWidth + 1;
    t.classList.toggle('is-fit', fits);
    this.bottom.classList.toggle('is-fit', fits);
    var ratio = t.clientWidth / t.scrollWidth;
    if (ratio >= 1) { this.progress.style.opacity = 0; return; }
    this.progress.style.opacity = 1;
    var max = t.scrollWidth - t.clientWidth;
    var frac = max > 0 ? t.scrollLeft / max : 0;
    var moveable = (1 - ratio) / ratio * 100;
    this.thumb.style.width = (ratio * 100) + '%';
    this.thumb.style.transform = 'translateX(' + (frac * moveable) + '%)';
  }
}
customElements.define('collection-showcase', CollectionShowcase);
